import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import API from '@/lib/api';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('reason') === 'inactivity') {
      toast.info('Sie wurden wegen Inaktivität automatisch abgemeldet.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim() || loading) return;

    setLoading(true);
    try {
      console.log("Attempting login to:", `${API}/auth/login`);
      const response = await axios.post(`${API}/auth/login`, {
        email: email.trim(),
        password: password
      });

      // Speichere Token im localStorage
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        if (response.data.refresh_token) {
          localStorage.setItem('refresh_token', response.data.refresh_token);
        }
        toast.success('Erfolgreich angemeldet');
        
        // Check if password change is required
        if (response.data.user?.must_change_password) {
          navigate('/change-password');
        } else {
          // Weiterleitung zum Admin-Dashboard
          navigate('/admin');
        }
      } else {
        toast.error('Kein Token erhalten');
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || 'Fehler beim Anmelden. Bitte versuchen Sie es erneut.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim() || forgotLoading) return;

    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/request-password-reset`, {
        email: forgotEmail.trim()
      });
      
      // Always show success message (prevents user enumeration)
      toast.success('Wenn ein Konto mit dieser E-Mail existiert, haben wir einen Link zum Zurücksetzen gesendet.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      console.error('Forgot password error:', error);
      // Still show generic message for security
      toast.success('Wenn ein Konto mit dieser E-Mail existiert, haben wir einen Link zum Zurücksetzen gesendet.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } finally {
      setForgotLoading(false);
    }
  };

  // Forgot Password Modal/Section
  if (showForgotPassword) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          background: 'radial-gradient(circle at top right, rgba(74, 144, 226, 0.08), transparent 55%), #ffffff'
        }}
      >
        <Card className="w-full max-w-md shadow-lg border-slate-200 bg-white">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center items-center gap-3 mb-2">
              <img
                src="/logo-stadtki.jpg"
                alt="StadtKI Logo"
                className="h-14 w-auto"
              />
              <div className="font-semibold text-lg tracking-wide uppercase" style={{ color: '#0b2f52' }}>
                stadtki
              </div>
            </div>
            <CardTitle className="text-2xl font-semibold" style={{ color: '#0b2f52' }}>
              Passwort vergessen?
            </CardTitle>
            <CardDescription className="text-slate-600">
              Geben Sie Ihre E-Mail-Adresse ein, um einen Link zum Zurücksetzen zu erhalten.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-slate-700">
                  E-Mail
                </Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="ihre@email.de"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  disabled={forgotLoading}
                  required
                  autoFocus
                  className="mt-1 bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 focus-visible:ring-sky-400"
                />
              </div>
              <Button
                type="submit"
                disabled={forgotLoading || !forgotEmail.trim()}
                className="w-full bg-sky-600 hover:bg-sky-500 text-white font-semibold shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {forgotLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Senden...
                  </>
                ) : (
                  'Link anfordern'
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowForgotPassword(false)}
                className="w-full text-slate-600 hover:text-slate-900"
              >
                Zurück zur Anmeldung
              </Button>
              <p className="text-xs text-slate-500 text-center mt-4">
                Zu viele Versuche? Bitte kontaktieren Sie Ihren Administrator.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: 'radial-gradient(circle at top right, rgba(74, 144, 226, 0.08), transparent 55%), #ffffff'
      }}
    >
      <Card className="w-full max-w-md shadow-lg border-slate-200 bg-white">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center items-center gap-3 mb-2">
            <img
              src="/logo-stadtki.jpg"
              alt="StadtKI Logo"
              className="h-14 w-auto"
            />
            <div className="font-semibold text-lg tracking-wide uppercase" style={{ color: '#0b2f52' }}>
              stadtki
            </div>
          </div>
          <CardTitle className="text-2xl font-semibold" style={{ color: '#0b2f52' }}>
            Login
          </CardTitle>
          <CardDescription className="text-slate-600">
            Melden Sie sich an, um Ihre StadtKI mit Ihren Dokumenten und Benutzern zu verwalten
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">
                E-Mail
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
                className="mt-1 bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 focus-visible:ring-sky-400"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">
                Passwort
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  required
                  className="mt-1 bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 focus-visible:ring-sky-400 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-700 focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading || !email.trim() || !password.trim()}
              className="w-full bg-sky-600 hover:bg-sky-500 text-white font-semibold shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Anmelden...
                </>
              ) : (
                'Anmelden'
              )}
            </Button>
            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-sky-600 hover:text-sky-500 hover:underline focus:outline-none"
              >
                Passwort vergessen?
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default LoginPage;
